import { Link } from 'react-router';
import type { PublicEventSummary } from '@gatecheckpro/contracts';
import {
  EmptyState,
  LoadingRegion,
  Notice,
  formatInZone,
  formatMoneyOrFree,
} from '@gatecheckpro/design-system';
import { Chrome } from './Chrome.tsx';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';
import { useSession } from '../lib/session.tsx';

/**
 * Event discovery.
 *
 * Real from Slice 2. The endpoint answers `AVAILABLE` whether or not anything is
 * listed, so this page can tell "nothing on sale" apart from "this feature does
 * not exist" — and only the second should make a visitor suspect something is
 * broken (kickoff Section 11).
 *
 * Nothing here is purchasable. That is said in words rather than implied by a
 * button that dead-ends.
 */
export function DiscoverPage() {
  const { state } = useSession();
  const { resource } = useResource<{ state: 'AVAILABLE'; events: PublicEventSummary[] }>(
    '/api/v1/public/events',
  );

  return (
    <Chrome>
      <section className="gc-hero">
        <div className="gc-container">
          <span className="gc-eyebrow">GateCheck</span>
          <h1 className="gc-hero__title">Your ticket works when the network doesn&rsquo;t.</h1>
          <p className="gc-hero__lede">
            GateCheck tickets are ownership records held by GateCheckPro, not pictures on a phone.
            At the gate they are checked against the venue itself — so a queue keeps moving through
            a network outage, and a ticket that has already been used stays used.
          </p>
        </div>
      </section>

      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose">
          {resource.status === 'loading' && <LoadingRegion label="Loading events" />}
          {resource.status === 'failed' && <FailureNotice error={resource.error} />}

          {resource.status === 'loaded' && (
            <>
              <div className="gc-stack gc-stack--tight">
                <h2>What&rsquo;s on</h2>
                <p className="gc-muted">Events organizers have published on GateCheck.</p>
              </div>

              {resource.data.events.length === 0 ? (
                <EmptyState title="Nothing on sale right now">
                  No organizer has a public event at the moment. This page is working — there is
                  simply nothing listed yet.
                </EmptyState>
              ) : (
                <div className="gc-grid">
                  {resource.data.events.map((event) => (
                    <Link key={event.slug} to={`/e/${event.slug}`} className="gc-event-card">
                      <span className="gc-event-card__title">{event.title}</span>
                      {event.publicSummary !== null && (
                        <span className="gc-muted">{event.publicSummary}</span>
                      )}
                      <span className="gc-muted">
                        {event.startsAt === null
                          ? 'Dates to be announced'
                          : formatInZone(event.startsAt, event.timezone, { withYear: true })}
                        {event.sessionCount > 1 && ` · ${String(event.sessionCount)} dates`}
                      </span>
                      {event.venue !== null && (
                        <span className="gc-muted">
                          {event.venue.name} · {event.venue.locality}
                        </span>
                      )}
                      {event.priceFrom !== null && (
                        <span className="gc-ticket-card__price">
                          from {formatMoneyOrFree(event.priceFrom)}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              )}

              <Notice tone="info" title="Ticket sales open in the next release">
                Events listed here are real and their prices are real. Buying is not live yet —
                GateCheck will not show you a checkout that goes nowhere.
              </Notice>
            </>
          )}

          {state.status === 'anonymous' && (
            <Notice tone="neutral" title="Create your account now">
              <div className="gc-stack gc-stack--tight">
                <span>
                  An account is how a ticket becomes yours rather than a file you happen to hold. It
                  is also how a lost phone stops being a lost ticket.
                </span>
                <div className="gc-row">
                  <Link to="/register" className="gc-button gc-button--primary">
                    Create an account
                  </Link>
                  <Link to="/sign-in" className="gc-button">
                    Sign in
                  </Link>
                </div>
              </div>
            </Notice>
          )}

          <section className="gc-grid">
            <article className="gc-card gc-stack gc-stack--tight">
              <span className="gc-eyebrow">Ownership</span>
              <h3>A ticket is a record, not an image</h3>
              <p className="gc-muted">
                Transfer it and the old code stops working. That is what makes a screenshot
                worthless to anyone but you.
              </p>
            </article>
            <article className="gc-card gc-stack gc-stack--tight">
              <span className="gc-eyebrow">GateMesh</span>
              <h3>The gate keeps working offline</h3>
              <p className="gc-muted">
                Scanners agree with each other over the venue&rsquo;s own network, so a ticket used
                at one entrance cannot be used again at another — internet or not.
              </p>
            </article>
            <article className="gc-card gc-stack gc-stack--tight">
              <span className="gc-eyebrow">Honesty</span>
              <h3>You will be told what is actually true</h3>
              <p className="gc-muted">
                Payment pending is not payment confirmed. GateCheck says which one you are looking
                at.
              </p>
            </article>
          </section>
        </div>
      </main>
    </Chrome>
  );
}
